/**
 * Typed Portal Shell API functions.
 *
 * Replaces all raw fetch() calls in portal-shell.tsx and
 * settings-view-wired.tsx with typed, single-responsibility functions
 * built on the portalGet/portalPost/etc. utilities from Phase 2.
 */
import { t, translateApiError } from '../../../../common/i18n.js'
import type { ExperienceEntry, ExperienceInput } from '../../../../common/portal-experience.js'
import type { LeaveCategory } from '../../../../common/types/portal-leave-types.js'
import type { ThemePreference } from '../../../../common/types/theme.js'
import type { OrgNode } from '../../../components/portal-org-mini-tree/portal-org-mini-tree.js'
import { getCurrentLanguage } from '../../../utils/language.js'
import type {
    CompensationData,
    CompensationLineItemGroup,
    CompensationLineItemInput,
    CompensationType,
    ContractRecord,
    CustomFieldWithValue,
    EmergencyContact,
    EmployeeDocument,
    EmployeeProfileData,
    EmploymentHistoryRecord,
    LeaveRequest,
    LeaveSettlement,
    ProfileLeaveBalance,
    WorkingPatternHistoryRecord,
} from '../views/employee-profile/employee-profile-view.js'
import type { EmployeeListItem } from '../views/people/people-view.js'
import {
    portalDelete,
    portalGet,
    portalGetPaginated,
    portalPatch,
    portalPost,
    portalPut,
} from './api.js'
import type { ApiResult, PaginatedResult } from './api.js'

// ── Types used across the shell ──

/** Scope-independent effective permission keys, as resolved by the server. */
export interface PortalPermissions {
    workspace: string[]
    profile: string[]
    admin: string[]
}

/** Broadest scope across the user's roles that grant time-away management. */
export type TimeAwayManageScope = 'own' | 'reports' | 'everyone' | null

export interface PortalMeData {
    userId: number
    employeeId: number | null
    googleDriveUrl: string | null
    avatarUrl: string | null
    /** Set while "View as" is active — identifies the real (admin) session owner. */
    impersonation: { realUserId: number; realUserName: string | null } | null
    permissions: PortalPermissions
    timeAwayManageScope: TimeAwayManageScope
    themePreference: ThemePreference
}

export interface ImpersonableUser {
    id: number
    name: string | null
    email: string
    avatar: string | null
}

export interface SettingsPolicy {
    id: number
    name: string
    leave_type_id: number
    leaveTypeName: string
    badgeType: string
    accrual_method: string
    annual_limit: number
    // null → does not carry over (expires at year end); N → usable N months
    // past the earned year's end.
    carryover_expiry_months: number | null
    allowance_rounding: 'half' | 'full' | 'none'
    min_notice_days: number
    min_duration_days: number
    // Longest single request in calendar days; null → no cap.
    max_duration_days: number | null
    must_take_full_week: boolean
    requires_approval: boolean
    applies_to: string
    is_active: boolean
    category: 'working' | 'time_off'
}

export interface SettingsHoliday {
    id: number
    date: string
    name: string
    company_id: number
    year: number
}

export interface SettingsCustomField {
    id: number
    name: string
    field_key: string
    field_type: string
    options: string | null
    visibility: string
}

export interface SettingsCompany {
    id: number
    name: string
    reg_code: string | null
    /** Full-time norm day (h) for BalanceTime — 7 at PLAY, 8 at NOPE. */
    standard_day_hours: number
}

export interface LeaveType {
    id: number
    name: string
}

export interface PolicyEmployee {
    id: number
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
}

export type SettingsSlideMode = 'closed' | 'create' | 'detail'

// ── Auth & Identity ──

export const fetchMyRole = (): Promise<ApiResult<PortalMeData>> =>
    portalGet<PortalMeData>('/api/portal/me')

// ── Dashboard ──

export interface FilterScope {
    company_id?: number
    team_id?: number
}

export interface DashboardFilterScopes {
    companies: { id: number; name: string }[]
    teams: { id: number; name: string; company_id: number | null }[]
}

// ── People ──

export interface EmployeeFilters {
    search?: string
    sort?: 'first_name' | 'last_name' | 'hire_date'
    dir?: 'asc' | 'desc'
    companyId?: number
    teamId?: number
    locationId?: number
    countryCode?: string
    managerId?: number
    status?: string
    /** Keep only employees currently active today (onboarded, not departed). */
    currentlyActive?: boolean
    limit?: number
    page?: number
}

export const fetchEmployees = (
    filters: EmployeeFilters = {},
): Promise<PaginatedResult<EmployeeListItem[]>> => {
    const params = new URLSearchParams()
    params.set('limit', String(filters.limit ?? 50))
    if (filters.page) params.set('page', String(filters.page))
    if (filters.search && filters.search.length >= 2) params.set('search', filters.search)
    if (filters.sort) params.set('sort', filters.sort)
    if (filters.dir) params.set('dir', filters.dir)
    if (filters.companyId) params.set('company_id', String(filters.companyId))
    if (filters.teamId) params.set('team_id', String(filters.teamId))
    if (filters.locationId) params.set('location_id', String(filters.locationId))
    if (filters.countryCode) params.set('country_code', filters.countryCode)
    if (filters.managerId) params.set('manager_id', String(filters.managerId))
    if (filters.status) params.set('status', filters.status)
    if (filters.currentlyActive) params.set('currently_active', '1')
    return portalGetPaginated<EmployeeListItem[]>(`/api/portal/employees?${params.toString()}`)
}

export const createEmployee = (data: Record<string, unknown>): Promise<ApiResult<unknown>> =>
    portalPost('/api/portal/employees', data)

// ── Reference Data ──

export const fetchCompanies = (): Promise<ApiResult<{ id: number; name: string }[]>> =>
    portalGet('/api/portal/companies')

// `currentlyActive` narrows to teams with at least one employee active today —
// pass it when the surface's roster is itself limited to currently active people.
export const fetchTeams = (options?: {
    currentlyActive?: boolean
}): Promise<ApiResult<{ id: number; name: string }[]>> =>
    portalGet(`/api/portal/teams${options?.currentlyActive ? '?currently_active=1' : ''}`)

export const fetchLocations = (): Promise<
    ApiResult<{ id: number; name: string; country_code: string }[]>
> => portalGet('/api/portal/locations')

// `currentlyActive` narrows to managers active today with at least one report
// active today — pass it when the surface's roster is limited to currently active people.
export const fetchManagers = (options?: {
    currentlyActive?: boolean
}): Promise<ApiResult<{ id: number; first_name: string; last_name: string }[]>> =>
    portalGet(`/api/portal/managers${options?.currentlyActive ? '?currently_active=1' : ''}`)

// All active company-scoped employees — feeds the "edit employment" manager
// picker (unlike fetchManagers, which lists only people who already have reports).
export const fetchEmployeeOptions = (): Promise<
    ApiResult<{ id: number; first_name: string; last_name: string }[]>
> => portalGet('/api/portal/employee-options')

export type UpcomingEventType =
    | 'birthday'
    | 'anniversary'
    | 'first_day'
    | 'probation_end'
    | 'offboarding'

export interface UpcomingEvent {
    type: UpcomingEventType
    employeeId: number
    date: string
    daysUntil: number
    years: number | null
    firstName: string
    lastName: string
    avatarUrl: string | null
    jobTitle: string | null
}

export const fetchPeopleEvents = (windowDays = 30): Promise<ApiResult<UpcomingEvent[]>> =>
    // `/api/portal/people-events` (not `/api/portal/events`, which is the Notion
    // culture-events feed) — see employee-routes.ts for the path-collision note.
    portalGet(`/api/portal/people-events?window=${windowDays}`)

// ── Employee Profile ──

export const fetchEmployee = (id: number): Promise<ApiResult<EmployeeProfileData>> =>
    portalGet(`/api/portal/employees/${id}`)

// ── Manager-confirmed field changes (work location) ──

export interface PendingFieldChange {
    id: number
    fieldKey: string
    newValue: string
    newLabel: string | null
    oldLabel: string | null
    createdAt: string
}

export const fetchFieldChangeRequests = (
    employeeId: number,
): Promise<ApiResult<{ pending: PendingFieldChange | null; canDecide: boolean }>> =>
    portalGet(`/api/portal/employees/${employeeId}/field-change-requests`)

export const requestLocationChange = (
    employeeId: number,
    locationId: number,
): Promise<ApiResult<{ id: number }>> =>
    portalPost(`/api/portal/employees/${employeeId}/field-change-requests`, {
        location_id: locationId,
    })

export const requestCustomLocationChange = (
    employeeId: number,
    name: string,
    countryCode: string,
    timezone?: string,
): Promise<ApiResult<{ id: number }>> =>
    portalPost(`/api/portal/employees/${employeeId}/field-change-requests`, {
        custom_name: name,
        custom_country_code: countryCode,
        ...(timezone ? { custom_timezone: timezone } : {}),
    })

export const decideFieldChangeRequest = (
    employeeId: number,
    requestId: number,
    action: 'approve' | 'reject',
): Promise<ApiResult<{ id: number; status: string }>> =>
    portalPost(`/api/portal/employees/${employeeId}/field-change-requests/${requestId}/decide`, {
        action,
    })

export const updateEmployee = (
    id: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/employees/${id}`, data)

// Flip an employee's lifecycle status (offboard / reactivate). Separate from the
// field PATCH above: gated server-side by the `offboard_users` permission and
// audited as EMPLOYEE_STATUS_CHANGED.
export const setEmployeeStatus = (
    id: number,
    status: 'active' | 'inactive' | 'on_leave',
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/employees/${id}/status`, { status })

// Record the leaving details ("Lõpeta töösuhe" form, TASK-403). Doesn't flip
// the status unless the end date is already past — the nightly offboarding
// sweep deactivates the person the day after their last day.
export const offboardEmployee = (
    id: number,
    data: {
        employment_end_date: string
        leaving_file: string | null
        turnover_impact: string | null
        leaving_reason: string | null
    },
): Promise<ApiResult<{ id: number; status: string }>> =>
    portalPost(`/api/portal/employees/${id}/offboard`, data)

export const fetchEmergencyContacts = (id: number): Promise<ApiResult<EmergencyContact[]>> =>
    portalGet(`/api/portal/employees/${id}/emergency-contacts`)

export const addEmergencyContact = (
    employeeId: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/employees/${employeeId}/emergency-contacts`, data)

export const deleteEmergencyContact = (
    employeeId: number,
    contactId: number,
): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/emergency-contacts/${contactId}`)

export const updateEmergencyContact = (
    employeeId: number,
    contactId: number,
    data: { name: string; relationship: string; phone: string },
): Promise<ApiResult<unknown>> =>
    portalPatch(`/api/portal/employees/${employeeId}/emergency-contacts/${contactId}`, data)

// ── Experiences ("Kogemused" repeater, TASK-300) ──

export const fetchExperiences = (id: number): Promise<ApiResult<ExperienceEntry[]>> =>
    portalGet(`/api/portal/employees/${id}/experiences`)

export const addExperience = (
    employeeId: number,
    data: ExperienceInput,
): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/employees/${employeeId}/experiences`, data)

export const updateExperience = (
    employeeId: number,
    entryId: number,
    data: Omit<ExperienceInput, 'type'>,
): Promise<ApiResult<unknown>> =>
    portalPatch(`/api/portal/employees/${employeeId}/experiences/${entryId}`, data)

export const deleteExperience = (employeeId: number, entryId: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/experiences/${entryId}`)

export const fetchEmployeeDocuments = (id: number): Promise<ApiResult<EmployeeDocument[]>> =>
    portalGet(`/api/portal/employees/${id}/documents`)

export const addEmployeeDocument = (
    employeeId: number,
    data: { name: string; drive_url: string; category?: string },
): Promise<ApiResult<unknown>> => portalPost(`/api/portal/employees/${employeeId}/documents`, data)

export const updateEmployeeDocument = (
    employeeId: number,
    documentId: number,
    // drive_url is omitted for file-backed documents (the stored file stays as-is)
    data: { name: string; drive_url?: string; category?: string },
): Promise<ApiResult<unknown>> =>
    portalPut(`/api/portal/employees/${employeeId}/documents/${documentId}`, data)

export const deleteEmployeeDocument = (
    employeeId: number,
    documentId: number,
): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/documents/${documentId}`)

// ── Google Drive (per-user metadata browser) ──

export const DRIVE_FOLDER_MIME = 'application/vnd.google-apps.folder'

export interface DriveFile {
    id: string
    name: string
    mimeType: string
    iconLink?: string
    thumbnailLink?: string
    webViewLink?: string
    modifiedTime?: string
    size?: string
    parents?: string[]
}

export interface DriveStatus {
    connected: boolean
    needsReconnect: boolean
    connectUrl: string
}

export interface DriveChildren {
    folder: { id: string; name: string | null; parents?: string[] }
    files: DriveFile[]
    nextPageToken: string | null
}

/** Extract a Drive folder id from a `…/folders/<id>…` URL, else null. */
export const parseDriveFolderId = (url: string | null | undefined): string | null =>
    url?.match(/\/folders\/([\w-]+)/)?.[1] ?? null

/** True when the URL points at Google Docs/Sheets/Slides (docs.google.com). */
export const isGoogleDocsUrl = (url: string | null | undefined): boolean =>
    /^https?:\/\/(?:[\w-]+\.)*docs\.google\.com\//i.test(url ?? '')

/** True when the URL points at Google Drive (drive.google.com). */
export const isGoogleDriveUrl = (url: string | null | undefined): boolean =>
    /^https?:\/\/(?:[\w-]+\.)*drive\.google\.com\//i.test(url ?? '')

export const fetchDriveStatus = (): Promise<ApiResult<DriveStatus>> =>
    portalGet('/api/portal/drive/status')

export const fetchDriveChildren = (
    folderId: string,
    pageToken?: string,
): Promise<ApiResult<DriveChildren>> =>
    portalGet(
        `/api/portal/drive/folders/${folderId}/children` +
            (pageToken ? `?pageToken=${encodeURIComponent(pageToken)}` : ''),
    )

export const disconnectDrive = (): Promise<ApiResult<void>> =>
    portalDelete('/api/portal/drive/disconnect')

export const fetchEmployeeTimeOff = (
    id: number,
): Promise<ApiResult<{ requests: LeaveRequest[]; balances: ProfileLeaveBalance[] }>> =>
    portalGet(`/api/portal/employees/${id}/time-off`)

export const fetchLeaveSettlement = (id: number): Promise<ApiResult<LeaveSettlement>> =>
    portalGet(`/api/portal/employees/${id}/leave-settlement`)

export interface CompanyHoliday {
    id: number
    date: string
    name: string
    year: number
}

export const fetchCompanyHolidays = (
    year: number,
    companyId?: number,
): Promise<ApiResult<CompanyHoliday[]>> =>
    portalGet(
        `/api/portal/company-holidays?year=${year}` + (companyId ? `&company_id=${companyId}` : ''),
    )

// Cancel a PENDING/APPROVED request (shared rules: time-off-edit-rules).
// Managers/admins cancel any request in place — taken absences included; the
// owner cancels their own pending or upcoming ones directly. An owner deleting
// a taken approval-flow absence gets `cancel_requested: true` back instead —
// the deletion awaits the manager. The server refunds the ledger on a real
// cancellation.
export const cancelTimeOffRequest = (
    requestId: number,
): Promise<ApiResult<{ id: number; status: string; cancel_requested?: boolean }>> =>
    portalPatch(`/api/portal/time-off/requests/${requestId}`, { action: 'cancel' })

// Approve or reject an owner's pending cancellation request (TASK-402).
// Requires time-away manage scope over the target; approve removes the absence
// and refunds the days, reject keeps it.
export const decideTimeOffCancellation = (
    requestId: number,
    action: 'approve' | 'reject',
): Promise<ApiResult<{ id: number; status: string }>> =>
    portalPatch(`/api/portal/time-off/requests/${requestId}`, {
        action: action === 'approve' ? 'approve_cancel' : 'reject_cancel',
    })

// Edit an existing PENDING/APPROVED request's dates, note or type. Owners may
// edit their own requests — changing the dates or the type of an approved
// approval-flow request reverts it to PENDING (the returned status reflects
// it), for concluded (taken) absences too; manage rights edit in place. The
// server re-validates the new type, re-calculates business days and reconciles
// ledger entries across the old and new policy.
export const editTimeOffRequest = (
    requestId: number,
    data: { start_date?: string; end_date?: string; note?: string; policy_id?: number },
): Promise<ApiResult<{ id: number; status: string; business_days: number }>> =>
    portalPut(`/api/portal/time-off/requests/${requestId}`, data)

// Approve or reject a PENDING request. Requires time-away manage scope over the
// target (the server guards self-approval). Mirrors the Slack approval flow and
// refreshes every posted Slack message on success.
export const decideTimeOffRequest = (
    requestId: number,
    action: 'approve' | 'reject',
    rejectionReason?: string,
): Promise<ApiResult<{ id: number; status: string }>> =>
    portalPatch(`/api/portal/time-off/requests/${requestId}`, {
        action,
        ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
    })

export const fetchEmployeeCustomFields = (id: number): Promise<ApiResult<CustomFieldWithValue[]>> =>
    portalGet(`/api/portal/employees/${id}/custom-fields`)

export const saveEmployeeCustomFields = (
    employeeId: number,
    values: Record<string, unknown>,
): Promise<ApiResult<unknown>> =>
    portalPut(`/api/portal/employees/${employeeId}/custom-fields`, { values })

export const fetchOrgTree = (
    id: number,
): Promise<ApiResult<{ manager: OrgNode | null; reports: OrgNode[] }>> =>
    portalGet(`/api/portal/employees/${id}/org-tree`)

// ── Compensation & Contracts ──

export const fetchCompensation = (employeeId: number): Promise<ApiResult<CompensationData>> =>
    portalGet(`/api/portal/employees/${employeeId}/compensation`)

export const fetchCompensationLineItems = (
    employeeId: number,
): Promise<ApiResult<CompensationLineItemGroup[]>> =>
    portalGet(`/api/portal/employees/${employeeId}/compensation-line-items`)

// Compensation types assignable to a line item (everything except salary).
export const fetchCompensationTypes = (): Promise<ApiResult<CompensationType[]>> =>
    portalGet(`/api/portal/compensation-types`)

export const createCompensationLineItem = (
    employeeId: number,
    data: CompensationLineItemInput,
): Promise<ApiResult<{ id: number }>> =>
    portalPost(`/api/portal/employees/${employeeId}/compensation-line-items`, data)

export const updateCompensationLineItem = (
    employeeId: number,
    itemId: number,
    data: Partial<CompensationLineItemInput>,
): Promise<ApiResult<{ id: number }>> =>
    portalPatch(`/api/portal/employees/${employeeId}/compensation-line-items/${itemId}`, data)

export const deleteCompensationLineItem = (
    employeeId: number,
    itemId: number,
): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/compensation-line-items/${itemId}`)

export const createCompensation = (
    employeeId: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/employees/${employeeId}/compensation`, data)

export const updateCompensation = (
    employeeId: number,
    compId: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> =>
    portalPatch(`/api/portal/employees/${employeeId}/compensation/${compId}`, data)

export const deleteCompensation = (employeeId: number, compId: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/compensation/${compId}`)

// Audit-only: records that an admin revealed (unmasked) the salary figures.
export const revealCompensation = (employeeId: number): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/employees/${employeeId}/reveal-compensation`, {})

export const fetchContracts = (employeeId: number): Promise<ApiResult<ContractRecord[]>> =>
    portalGet(`/api/portal/employees/${employeeId}/contracts`)

export const createContract = (
    employeeId: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> => portalPost(`/api/portal/employees/${employeeId}/contracts`, data)

// In-place correction of the active employment record's anchor dates,
// probation end, and collaboration form.
export const updateEmploymentDates = (
    employeeId: number,
    data: {
        hire_date?: string | null
        contract_start?: string | null
        probation_end_date?: string | null
        collaboration_type?: string | null
    },
): Promise<ApiResult<unknown>> =>
    portalPatch(`/api/portal/employees/${employeeId}/employment-records`, data)

export const fetchEmploymentHistory = (
    employeeId: number,
): Promise<ApiResult<EmploymentHistoryRecord[]>> =>
    portalGet(`/api/portal/employees/${employeeId}/employment-history`)

export const fetchWorkingPatterns = (
    employeeId: number,
): Promise<ApiResult<WorkingPatternHistoryRecord[]>> =>
    portalGet(`/api/portal/employees/${employeeId}/working-patterns`)

export const deleteEmploymentRecord = (
    employeeId: number,
    recordId: number,
): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/employment-records/${recordId}`)

// Create a new SCD2 employment record (admin) — team/location/manager/work_mode/
// job_title change effective from a chosen date. The server closes the prior
// active record and recomputes Manager-role membership for affected managers.
export const createEmploymentRecord = (
    employeeId: number,
    data: Record<string, unknown>,
): Promise<ApiResult<{ id: number }>> =>
    portalPost(`/api/portal/employees/${employeeId}/employment-records`, data)

// ── View as (impersonation) ──

export const fetchImpersonableUsers = (): Promise<ApiResult<ImpersonableUser[]>> =>
    portalGet<ImpersonableUser[]>('/api/portal/impersonation/users')

export const startImpersonation = (userId: number): Promise<ApiResult<unknown>> =>
    portalPost('/api/portal/impersonation/start', { userId })

export const stopImpersonation = (): Promise<ApiResult<unknown>> =>
    portalPost('/api/portal/impersonation/stop', {})

// ── Settings: General ──

export const fetchSettingsGeneral = (): Promise<ApiResult<{ companies: SettingsCompany[] }>> =>
    portalGet('/api/portal/settings/general')

// ── Settings: Leave Policies ──

export const fetchLeavePolicies = (): Promise<ApiResult<SettingsPolicy[]>> =>
    portalGet('/api/portal/settings/leave-policies')

export const fetchLeaveTypes = (): Promise<ApiResult<LeaveType[]>> =>
    portalGet('/api/portal/leave-types')

export const createLeavePolicy = (
    data: Record<string, unknown>,
): Promise<ApiResult<{ id: number }>> => portalPost('/api/portal/settings/leave-policies', data)

export const updateLeavePolicy = (
    id: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/leave-policies/${id}`, data)

export const deleteLeavePolicy = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/leave-policies/${id}`)

export const fetchPolicyEmployees = (policyId: number): Promise<ApiResult<PolicyEmployee[]>> =>
    portalGet(`/api/portal/settings/leave-policies/${policyId}/employees`)

export const savePolicyEmployees = (
    policyId: number,
    employeeIds: number[],
): Promise<ApiResult<unknown>> =>
    portalPut(`/api/portal/settings/leave-policies/${policyId}/employees`, {
        employee_ids: employeeIds,
    })

// ── Settings: Leave Policy Bundles (puhkusepoliitikad) ──

export interface BundleItem {
    bundle_id: number
    policy_id: number
    name: string
    annual_limit: number
    accrual_method: string
    requires_approval: boolean
    leaveTypeName: string
    badgeType: string
    category: 'working' | 'time_off'
}

export interface LeavePolicyBundle {
    id: number
    slug: string
    name: string
    is_active: boolean
    employee_count: number
    items: BundleItem[]
}

export interface BundleMember {
    id: number
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
}

export const fetchLeavePolicyBundles = (): Promise<ApiResult<LeavePolicyBundle[]>> =>
    portalGet('/api/portal/settings/leave-policy-bundles')

export const createLeavePolicyBundle = (data: {
    name: string
    policy_ids: number[]
}): Promise<ApiResult<{ id: number }>> =>
    portalPost('/api/portal/settings/leave-policy-bundles', data)

export const updateLeavePolicyBundle = (
    id: number,
    data: { name?: string; is_active?: boolean; policy_ids?: number[] },
): Promise<ApiResult<unknown>> =>
    portalPatch(`/api/portal/settings/leave-policy-bundles/${id}`, data)

export const deleteLeavePolicyBundle = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/leave-policy-bundles/${id}`)

export const fetchBundleEmployees = (id: number): Promise<ApiResult<BundleMember[]>> =>
    portalGet(`/api/portal/settings/leave-policy-bundles/${id}/employees`)

export const saveBundleEmployees = (
    id: number,
    employeeIds: number[],
): Promise<ApiResult<unknown>> =>
    portalPut(`/api/portal/settings/leave-policy-bundles/${id}/employees`, {
        employee_ids: employeeIds,
    })

// ── Employee leave-policy bundle (profile Eemalolek tab, TASK-348) ──

export interface EmployeeLeaveBundle {
    bundle_id: number | null
    effective_from: string | null
    bundles: { id: number; name: string }[]
}

export const fetchEmployeeLeaveBundle = (
    employeeId: number,
): Promise<ApiResult<EmployeeLeaveBundle>> =>
    portalGet(`/api/portal/employees/${employeeId}/leave-bundle`)

export const saveEmployeeLeaveBundle = (
    employeeId: number,
    data: { bundle_id: number | null; effective_from?: string | null },
): Promise<ApiResult<unknown>> =>
    portalPut(`/api/portal/employees/${employeeId}/leave-bundle`, data)

// ── Time away: admin balance correction (TASK-567) ──

export interface LeaveBalanceAsOf {
    balance: number
    /** earned = main-vacation "balance as of" figure; available = bookable remainder */
    kind: 'earned' | 'available'
    /** Main vacation only: today's "Saadaval kuni 31.12" figure */
    availableUntilYearEnd?: number
}

export const fetchLeaveBalanceAsOf = (
    employeeId: number,
    policyId: number,
    date: string,
): Promise<ApiResult<LeaveBalanceAsOf>> =>
    portalGet(
        `/api/portal/employees/${employeeId}/leave-balance-as-of?policy_id=${policyId}&date=${encodeURIComponent(date)}`,
    )

export const saveLeaveBalanceAdjustment = (
    employeeId: number,
    data: { policy_id: number; amount: number; effective_date: string; note: string },
): Promise<ApiResult<{ adjustment: number; newBalance: number }>> =>
    portalPost(`/api/portal/employees/${employeeId}/leave-ledger`, data)

// ── Settings: Holidays ──

export const fetchHolidays = (
    year: number,
    companyId: number,
): Promise<ApiResult<SettingsHoliday[]>> =>
    portalGet(`/api/portal/settings/holidays?year=${year}&company_id=${companyId}`)

export const addHoliday = (data: {
    date: string
    name: string
    company_id: number
}): Promise<ApiResult<unknown>> => portalPost('/api/portal/settings/holidays', data)

export const updateHoliday = (
    id: number,
    data: { date?: string; name?: string },
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/holidays/${id}`, data)

export const deleteHoliday = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/holidays/${id}`)

// ── Settings: Locations (M10.2) ──

export interface SettingsLocation {
    id: number
    name: string
    country_code: string
    created_at_utc: string
    employee_count: number
}

export const fetchSettingsLocations = (): Promise<ApiResult<SettingsLocation[]>> =>
    portalGet('/api/portal/settings/locations')

export const createSettingsLocation = (data: {
    name: string
    country_code: string
}): Promise<ApiResult<{ id: number }>> => portalPost('/api/portal/settings/locations', data)

export const updateSettingsLocation = (
    id: number,
    data: { name?: string; country_code?: string },
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/locations/${id}`, data)

export const deleteSettingsLocation = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/locations/${id}`)

// ── Settings: Teams (M10.1) ──

export interface SettingsTeam {
    id: number
    name: string
    company_id: number | null
    member_count: number
    created_at_utc: string
}

export interface SettingsTeamMember {
    id: number
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
}

export const fetchSettingsTeams = (companyId?: number): Promise<ApiResult<SettingsTeam[]>> => {
    const params = companyId ? `?company_id=${companyId}` : ''
    return portalGet(`/api/portal/settings/teams${params}`)
}

export const createSettingsTeam = (data: {
    name: string
    company_id: number
}): Promise<ApiResult<{ id: number }>> => portalPost('/api/portal/settings/teams', data)

export const updateSettingsTeam = (
    id: number,
    data: { name?: string; company_id?: number },
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/teams/${id}`, data)

export const deleteSettingsTeam = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/teams/${id}`)

export const fetchTeamMembers = (teamId: number): Promise<ApiResult<SettingsTeamMember[]>> =>
    portalGet(`/api/portal/settings/teams/${teamId}/members`)

export const addTeamMember = (teamId: number, employeeId: number): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/settings/teams/${teamId}/members`, { employee_id: employeeId })

export const removeTeamMember = (teamId: number, employeeId: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/teams/${teamId}/members/${employeeId}`)

// ── Settings: Leave Types (M10.3) ──

export interface SettingsLeaveType {
    id: number
    slug: string
    name: string
    badge_type: string
    is_paid: boolean
    accrues_leave: boolean
    category: LeaveCategory
    position: number
    policy_count: number
    created_at_utc: string
}

export const fetchSettingsLeaveTypes = (): Promise<ApiResult<SettingsLeaveType[]>> =>
    portalGet('/api/portal/settings/leave-types')

export const createSettingsLeaveType = (data: {
    name: string
    slug?: string
    badge_type: string
    is_paid?: boolean
    accrues_leave?: boolean
    category?: LeaveCategory
}): Promise<ApiResult<{ id: number; slug: string }>> =>
    portalPost('/api/portal/settings/leave-types', data)

export const updateSettingsLeaveType = (
    id: number,
    data: {
        name?: string
        badge_type?: string
        is_paid?: boolean
        accrues_leave?: boolean
        category?: LeaveCategory
    },
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/leave-types/${id}`, data)

export const deleteSettingsLeaveType = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/leave-types/${id}`)

export const reorderSettingsLeaveTypes = (ids: number[]): Promise<ApiResult<unknown>> =>
    portalPut('/api/portal/settings/leave-types/order', { ids })

// ── Settings: Working Patterns (M10.4) ──

export interface SettingsWorkingPattern {
    id: number
    humaans_id: string | null
    name: string
    summary: string | null
    period_type: string | null
    full_time_hours: number | null
    standard_day_hours: number | null
    working_hours: number | null
    /** JSON-encoded string; parse to get a length-7 array. */
    working_pattern: string
    fte: number | null
    is_archived: boolean
    active_allocations_count: number
    created_at_utc: string
}

export const fetchSettingsWorkingPatterns = (
    includeArchived = false,
): Promise<ApiResult<SettingsWorkingPattern[]>> => {
    const params = includeArchived ? '?include_archived=true' : ''
    return portalGet(`/api/portal/settings/working-patterns${params}`)
}

export const createSettingsWorkingPattern = (data: {
    name: string
    summary?: string
    working_pattern: (number | boolean)[]
    standard_day_hours?: number
    full_time_hours?: number
    fte?: number
}): Promise<ApiResult<{ id: number }>> => portalPost('/api/portal/settings/working-patterns', data)

export const updateSettingsWorkingPattern = (
    id: number,
    data: {
        name?: string
        summary?: string | null
        working_pattern?: (number | boolean)[]
        standard_day_hours?: number
        full_time_hours?: number
        fte?: number
    },
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/working-patterns/${id}`, data)

export const archiveSettingsWorkingPattern = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/working-patterns/${id}`)

export const restoreSettingsWorkingPattern = (id: number): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/settings/working-patterns/${id}/restore`, {})

// ── Settings: Compensation Types (Lisatasud line-item picker) ──

export interface SettingsCompensationType {
    id: number
    /** NULL for admin-created types; set for Humaans-imported ones. */
    humaans_id: string | null
    name: string
    /** One of: bonus, commission, equity, custom (salary is never listed here). */
    base_type: string | null
    is_archived: boolean
    usage_count: number
    created_at_utc: string
}

export const fetchSettingsCompensationTypes = (
    includeArchived = false,
): Promise<ApiResult<SettingsCompensationType[]>> => {
    const params = includeArchived ? '?include_archived=true' : ''
    return portalGet(`/api/portal/settings/compensation-types${params}`)
}

export const createSettingsCompensationType = (data: {
    name: string
    base_type?: string
}): Promise<ApiResult<{ id: number }>> =>
    portalPost('/api/portal/settings/compensation-types', data)

export const updateSettingsCompensationType = (
    id: number,
    data: { name?: string; base_type?: string },
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/compensation-types/${id}`, data)

export const archiveSettingsCompensationType = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/compensation-types/${id}`)

export const restoreSettingsCompensationType = (id: number): Promise<ApiResult<unknown>> =>
    portalPost(`/api/portal/settings/compensation-types/${id}/restore`, {})

export const assignWorkingPatternAllocation = (
    employeeId: number,
    data: {
        working_pattern_id?: number
        effective_from: string
        // Per-person custom arrangement instead of an existing pattern. The
        // editor sends an explicit Mon-first day grid: numbers for `fixed`
        // (hours/day), booleans for `flex` (working days) plus a weekly total.
        custom?: {
            mode: 'fixed' | 'flex'
            working_pattern: (number | boolean)[]
            name?: string
            full_time_hours?: number
            standard_day_hours?: number
            weekly_hours?: number
        }
    },
): Promise<ApiResult<{ id: number }>> =>
    portalPost(`/api/portal/employees/${employeeId}/working-pattern-allocations`, data)

export const deleteWorkingPatternAllocation = (
    employeeId: number,
    allocationId: number,
): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/employees/${employeeId}/working-pattern-allocations/${allocationId}`)

// ── Settings: Custom Fields ──

export const fetchSettingsCustomFields = (): Promise<ApiResult<SettingsCustomField[]>> =>
    portalGet('/api/portal/settings/custom-fields')

export const createCustomField = (data: Record<string, unknown>): Promise<ApiResult<unknown>> =>
    portalPost('/api/portal/settings/custom-fields', data)

export const updateCustomField = (
    id: number,
    data: Record<string, unknown>,
): Promise<ApiResult<unknown>> => portalPatch(`/api/portal/settings/custom-fields/${id}`, data)

export const deleteCustomField = (id: number): Promise<ApiResult<void>> =>
    portalDelete(`/api/portal/settings/custom-fields/${id}`)

// ── RBAC: Roles (configurator) ──

export type RoleScopeType = 'own' | 'reports' | 'everyone'

export interface RoleListItem {
    id: number
    name: string
    description: string | null
    is_system: boolean
    scope_type: RoleScopeType
    member_count: number
}

export interface RoleFieldPermission {
    field_key: string
    can_view: boolean
    can_edit: boolean
    // Edit on the holder's own profile only ("muuda enda"), scope-independent.
    can_edit_own: boolean
}

export interface RoleCustomFieldPermission {
    custom_field_def_id: number
    can_view: boolean
    can_edit: boolean
}

export interface RoleDocTypePermission {
    doc_type: string
    can_view: boolean
    can_edit: boolean
}

export interface RoleLeaveTypePermission {
    leave_type_id: number
    can_view: boolean
    can_manage: boolean
}

export interface RolePermissionsBundle {
    workspace: string[]
    profile: string[]
    admin: string[]
    fields: RoleFieldPermission[]
    customFields: RoleCustomFieldPermission[]
    docTypes: RoleDocTypePermission[]
    leaveTypes: RoleLeaveTypePermission[]
    timeAway: { canViewDetails: boolean; canManage: boolean }
}

export interface RoleMember {
    user_id: number
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
    // Company this grant is scoped to (null = all companies).
    company_id: number | null
    company_name: string | null
}

export interface RoleCompany {
    id: number
    slug: string
    name: string
}

export interface RoleDetail {
    role: Omit<RoleListItem, 'member_count'>
    permissions: RolePermissionsBundle | null
    members: RoleMember[]
    // Companies available for the member-scope picker + the acting admin's own
    // scope ('all' = unrestricted, otherwise the company ids they may grant).
    companies: RoleCompany[]
    companyScope: 'all' | number[]
}

export interface CreateRolePayload {
    name: string
    description?: string | null
    scope_type: RoleScopeType
}

export interface UpdateRolePayload {
    name?: string
    description?: string | null
    scope_type?: RoleScopeType
    workspace_permissions?: string[]
    profile_permissions?: string[]
    admin_permissions?: string[]
    field_permissions?: RoleFieldPermission[]
    custom_field_permissions?: RoleCustomFieldPermission[]
    doc_type_permissions?: RoleDocTypePermission[]
    leave_type_permissions?: RoleLeaveTypePermission[]
    time_away?: { can_view_details: boolean; can_manage: boolean }
}

export const fetchRoles = (): Promise<ApiResult<{ roles: RoleListItem[] }>> =>
    portalGet('/api/portal/roles')

export const fetchRoleDetail = (id: number): Promise<ApiResult<RoleDetail>> =>
    portalGet(`/api/portal/roles/${id}`)

export const createRole = (payload: CreateRolePayload): Promise<ApiResult<{ id: number }>> =>
    portalPost('/api/portal/roles', payload)

export const updateRole = (
    id: number,
    payload: UpdateRolePayload,
): Promise<ApiResult<{ ok: boolean }>> => portalPatch(`/api/portal/roles/${id}`, payload)

export const deleteRole = (id: number): Promise<ApiResult<{ ok: boolean }>> =>
    portalDelete(`/api/portal/roles/${id}`)

export const addRoleMember = (
    roleId: number,
    userId: number,
    // undefined = let the server auto-derive (legacy); null = all companies.
    companyId?: number | null,
): Promise<ApiResult<{ ok: boolean }>> =>
    portalPost(
        `/api/portal/roles/${roleId}/members/${userId}`,
        companyId === undefined ? {} : { company_id: companyId },
    )

export const removeRoleMember = (
    roleId: number,
    userId: number,
): Promise<ApiResult<{ ok: boolean }>> =>
    portalDelete(`/api/portal/roles/${roleId}/members/${userId}`)

/** Search employees for role member candidates. Returns user_id where available. */
export const fetchRoleMemberCandidates = async (
    query: string,
): Promise<ApiResult<Array<{ user_id: number; first_name: string; last_name: string }>>> => {
    const params = new URLSearchParams()
    params.set('search', query)
    params.set('limit', '10')
    params.set('include_user_id', '1')
    return portalGet(`/api/portal/settings/member-search?${params.toString()}`)
}

// ── Avatar ──

/** Upload an avatar image file and return its storage URL. */
export const uploadAvatarImage = async (file: File): Promise<ApiResult<{ imageUrl: string }>> => {
    const formData = new FormData()
    formData.append('image', file)
    try {
        const res = await fetch('/api/upload/avatar', { method: 'POST', body: formData })
        if (!res.ok) {
            return { ok: false, error: t.portal.profile.avatarUploadFailed(getCurrentLanguage()) }
        }
        const json = (await res.json()) as { imageUrl: string }
        return { ok: true, data: json }
    } catch {
        return { ok: false, error: translateApiError('NETWORK_ERROR', {}, getCurrentLanguage()) }
    }
}

/** Save the avatar URL on the current user's employee record (or clear it). */
export const saveMyAvatar = (avatarUrl: string | null): Promise<ApiResult<unknown>> =>
    portalPut('/api/portal/me/avatar', { avatarUrl })

/** Admin: save (or clear) the avatar on another employee's record. */
export const saveEmployeeAvatar = (
    employeeId: number,
    avatarUrl: string | null,
): Promise<ApiResult<unknown>> =>
    portalPut(`/api/portal/employees/${employeeId}/avatar`, { avatarUrl })
